// The projectile gobs every combat unit fires: TankShotGob (WLineIterator flight, the
// DELAYED kmidHit contract — damage arrives via SendDelayedMsg timed by steps-remaining,
// identical on every client), BulletGob (faster, bullet anim), RocketGob (own class:
// slower, gid-consuming trail puffs every other step). game/Tank.cpp:603-846 +
// game/SRInfantry.cpp:171-215 + game/LRInfantry.cpp:148-392 + BringInBounds
// (game/misc.cpp:160-174).

import type { SimContext } from '../context.ts';
import { Gob, kfGobStateMachine, kfGobLayerSmokeFire } from './gob.ts';
import { kgtTankShot, kgtBullet, kgtRocket } from './gobtypes.ts';
import { knLimitSupport } from './gobmgr.ts';
import { WLineIterator } from '../math/wline.ts';
import { CalcDir } from '../math/dir.ts';
import { WcFromTc } from '../math/coords.ts';
import { kwcTile } from './unitconsts.ts';
import { idiv } from '../math/int32.ts';
import { Animation, kfAniDone, kfAniLoop, AnimData } from '../anim/animation.ts';
import {
  Message,
  newMessage,
  knHandled,
  knNotHandled,
  knDeleted,
  kstReservedGlobal,
  kmidReservedUpdate,
  kmidHit,
  ksmidNull,
} from '../sm/message.ts';
import { CreateAnimGob, kfAnmDeleteWhenDone, kfAnmSmokeFireLayer } from './animgob.ts';
import { kcmsUpdate } from '../math/coords.ts';
import type { UnitGob } from './unitgob.ts';

// game/ht.h:7669
export const kwcTankShotRate = kwcTile / 2;

// game/misc.cpp:160-174 — clamp into the map (units fire from special points that can
// hang off the edge)
export function BringInBounds(ctx: SimContext, wx: number, wy: number): { wx: number; wy: number } {
  const { ctx: cxMap, cty: cyMap } = ctx.gobm.GetMapSize();
  const cwxMap = WcFromTc(cxMap);
  const cwyMap = WcFromTc(cyMap);
  if (wx < 0) wx = 0;
  else if (wx >= cwxMap) wx = cwxMap - 1;
  if (wy < 0) wy = 0;
  else if (wy >= cwyMap) wy = cwyMap - 1;
  return { wx, wy };
}

export class TankShotGob extends Gob {
  m_li = new WLineIterator();
  m_gidOwner = 0;
  m_gidTarget = 0;
  m_nDamage = 0;
  m_ani = new Animation();

  // game/Tank.cpp:643-646
  constructor(ctx: SimContext) {
    super(ctx);
    this.m_ff = (this.m_ff | kfGobStateMachine | kfGobLayerSmokeFire) >>> 0;
  }

  override GetType(): number {
    return kgtTankShot;
  } // :728-731

  // game/Tank.cpp:648-683
  InitShot(
    panid: AnimData,
    wx: number,
    wy: number,
    wxTarget: number,
    wyTarget: number,
    nDamage: number,
    gidOwner: number,
    gidTarget: number,
    wcMoveRate: number,
  ): boolean {
    ({ wx, wy } = BringInBounds(this.ctx, wx, wy));
    ({ wx: wxTarget, wy: wyTarget } = BringInBounds(this.ctx, wxTarget, wyTarget));

    this.m_gidOwner = gidOwner;
    this.m_gidTarget = gidTarget;
    this.m_nDamage = nDamage;

    this.m_li.Init(wx, wy, wxTarget, wyTarget, wcMoveRate);

    // The iterator starts at the first step-integral point on the line
    this.m_wx = this.m_li.GetWX();
    this.m_wy = this.m_li.GetWY();

    this.m_ani.Init(panid);
    // HACK (the C++'s): an 8-frame strip 0 means one frame per 8-direction
    if (this.m_ani.GetAnimationData().GetFrameCount(0) === 8) {
      this.StartAnimation(this.m_ani, 0, CalcDir(wxTarget - wx, wyTarget - wy), kfAniDone);
    }

    return true;
  }

  // game/Tank.cpp:685-714
  Launch(): void {
    if (this.m_ani.GetAnimationData().GetFrameCount(0) !== 8) {
      this.StartAnimation(this.m_ani, 0, 0, kfAniLoop);
    }

    this.ctx.gobm.AddGob(this);

    // The hit arrives via a DELAYED MESSAGE timed by the shooter's step count — the same
    // number of updates on every client. (The C++ BUGBUG about resolution-dependent step — sim-quirks A6 —
    // counts is part of the contract; firing points are sim coordinates here.)
    const msgT = newMessage(kmidHit, this.m_gidOwner, this.m_gidTarget);
    msgT.Hit.gidAssailant = this.m_gidOwner;
    msgT.Hit.sideAssailant = this.ctx.gobm.GetGob(this.m_gidOwner)!.GetSide();
    msgT.Hit.nDamage = this.m_nDamage;

    this.ctx.smm!.SendDelayedMsg(msgT, (this.m_li.GetStepsRemaining() + 1) * idiv(kcmsUpdate, 10));

    const pgobTarget = this.ctx.gobm.GetGob(this.m_gidTarget);
    if (pgobTarget !== null) {
      this.ctx.host.NotifyShotFired(
        msgT.Hit.sideAssailant,
        pgobTarget.GetSide(),
        this.m_wx,
        this.m_wy,
      );
    }
  }

  // issue #98: unsavable in the C++-FAITHFUL format (shots are never SP-saved), but the TS
  // COMPLETE snapshot DOES carry them so an in-flight shot resumes bit-exact (its gid lifecycle
  // + the gidMap entry would otherwise diverge). ctx.snapshotComplete is only set during
  // snapshot()/restore(), so faithful saves still exclude it.
  IsSavable(): boolean {
    return this.ctx.snapshotComplete;
  } // :723-726

  /** The hardcoded per-type shot anim (BulletGob/ArtilleryShotGob override). Re-Init'd on load. */
  protected aniName(): string {
    return 'tankshot.anir';
  }

  override SaveState(pstm: import('../sm/stream.ts').Stream): boolean {
    pstm.WriteByte(1);
    this.m_li.SaveState(pstm);
    pstm.WriteWord(this.m_gidOwner);
    pstm.WriteWord(this.m_gidTarget);
    pstm.WriteWord(this.m_nDamage);
    this.m_ani.SaveState(pstm);
    return super.SaveState(pstm);
  }

  override LoadState(pstm: import('../sm/stream.ts').Stream): boolean {
    if (pstm.ReadByte() !== 1) return false;
    this.m_li.LoadState(pstm);
    this.m_gidOwner = pstm.ReadWord();
    this.m_gidTarget = pstm.ReadWord();
    this.m_nDamage = pstm.ReadWord();
    this.m_ani.Init(this.ctx.animDataFor(this.aniName()));
    this.m_ani.LoadState(pstm);
    return super.LoadState(pstm);
  }

  // game/Tank.cpp:776-844 — strip 0 = travel, strip 1 = impact (hardcoded)
  override ProcessStateMachineMessage(st: number, pmsg: Message): number {
    if (st === kstReservedGlobal && pmsg.mid === kmidReservedUpdate) {
      if (this.m_ani.GetStrip() === 0) {
        this.AdvanceAnimation(this.m_ani);
        this.MarkRedraw();

        if (this.m_li.Step()) {
          const wxOld = this.m_wx;
          const wyOld = this.m_wy;
          this.m_wx = this.m_li.GetWX();
          this.m_wy = this.m_li.GetWY();

          if (this.m_wx !== wxOld || this.m_wy !== wyOld) {
            this.ctx.gobm.MoveGob(this, wxOld, wyOld, this.m_wx, this.m_wy);
          }

          this.m_unvl.MinSkip();
        } else {
          // Impact sfx comes from the OWNER's consts (host). C++-faithful
          // recycled-gid bound (Tank.cpp:808-812 blind UnitGob cast): if the
          // firer died mid-flight and its gid slot was RECYCLED by another
          // ACTIVE unit, GetGob returns that unit and the WRONG impact sfx
          // plays — exactly the C++ behavior (parity-audit #9). A non-unit
          // recycle returns null through the active-only GetGob (only units
          // set kfGobActive) and stays silent — also the C++ outcome.
          const punt = this.ctx.gobm.GetGob(this.m_gidOwner) as UnitGob | null;
          if (punt !== null) {
            this.ctx.host.PlaySfx(punt.GetConsts().sfxImpact);
          }

          this.StartAnimation(this.m_ani, 1, 0, 0);
        }
        // (the kfPerfShots redraw-flag clear is presentation)
      } else {
        if (!this.AdvanceAnimation(this.m_ani)) {
          this.ctx.gobm.RemoveGob(this);
          return knDeleted;
        }
        // (kfPerfShotImpacts redraw clear: presentation)
      }
      return knHandled;
    }
    return knNotHandled;
  }
}

// game/Tank.cpp:610-627
export function CreateTankShotGob(
  ctx: SimContext,
  wx: number,
  wy: number,
  wxTarget: number,
  wyTarget: number,
  nDamage: number,
  gidOwner: number,
  gidTarget: number,
): TankShotGob | null {
  if (!ctx.gobm.IsBelowLimit(knLimitSupport)) return null;

  const pgob = new TankShotGob(ctx);
  if (
    !pgob.InitShot(
      ctx.animDataFor('tankshot.anir'),
      wx,
      wy,
      wxTarget,
      wyTarget,
      nDamage,
      gidOwner,
      gidTarget,
      kwcTankShotRate,
    )
  )
    return null;

  pgob.Launch();
  return pgob;
}

// game/SRInfantry.cpp:171-215 + ht.h:7707-7715 — a faster TankShot with the bullet anim
export class BulletGob extends TankShotGob {
  override GetType(): number {
    return kgtBullet;
  }
  protected override aniName(): string {
    return 'bullet.anir';
  }
}

// game/SRInfantry.cpp:178-196 (the fEagle param is vestigial — unused in the C++ body)
export function CreateBulletGob(
  ctx: SimContext,
  wx: number,
  wy: number,
  wxTarget: number,
  wyTarget: number,
  nDamage: number,
  gidOwner: number,
  gidTarget: number,
  _fEagle: boolean = false,
): BulletGob | null {
  if (!ctx.gobm.IsBelowLimit(knLimitSupport)) return null;

  const pgob = new BulletGob(ctx);
  if (
    !pgob.InitShot(
      ctx.animDataFor('bullet.anir'),
      wx,
      wy,
      wxTarget,
      wyTarget,
      nDamage,
      gidOwner,
      gidTarget,
      kwcTile,
    )
  )
    return null;

  pgob.Launch();
  return pgob;
}

// game/LRInfantry.cpp:148-392 — its own Gob: slower flight, 8-dir frame, and TRAIL PUFFS
// (gid-consuming AnimGobs) every other step while kfPerfRocketTrails is set (pinned ON)
export class RocketGob extends Gob {
  m_li = new WLineIterator();
  m_gidOwner = 0;
  m_gidTarget = 0;
  m_nDamage = 0;
  m_ani = new Animation();

  // game/LRInfantry.cpp:185-188
  constructor(ctx: SimContext) {
    super(ctx);
    this.m_ff = (this.m_ff | kfGobStateMachine | kfGobLayerSmokeFire) >>> 0;
  }

  override GetType(): number {
    return kgtRocket;
  } // :263-266

  // game/LRInfantry.cpp:190-249 — Init ALSO adds the gob and schedules the delayed hit
  InitRocket(
    wx: number,
    wy: number,
    wxTarget: number,
    wyTarget: number,
    nDamage: number,
    gidOwner: number,
    gidTarget: number,
  ): boolean {
    ({ wx, wy } = BringInBounds(this.ctx, wx, wy));

    this.m_gidOwner = gidOwner;
    this.m_gidTarget = gidTarget;
    this.m_nDamage = nDamage;

    this.m_li.Init(wx, wy, wxTarget, wyTarget, idiv(kwcTile, 3));

    this.m_wx = this.m_li.GetWX();
    this.m_wy = this.m_li.GetWY();

    this.m_ani.Init(this.ctx.animDataFor('rocket.anir'));
    this.StartAnimation(this.m_ani, 0, CalcDir(wxTarget - wx, wyTarget - wy), kfAniDone);

    this.ctx.gobm.AddGob(this);

    const msgT = newMessage(kmidHit, this.m_gidOwner, this.m_gidTarget);
    msgT.Hit.gidAssailant = this.m_gidOwner;
    msgT.Hit.sideAssailant = this.ctx.gobm.GetGob(this.m_gidOwner)!.GetSide();
    msgT.Hit.nDamage = this.m_nDamage;
    this.ctx.smm!.SendDelayedMsg(msgT, (this.m_li.GetStepsRemaining() + 1) * idiv(kcmsUpdate, 10));

    const pgobTarget = this.ctx.gobm.GetGob(this.m_gidTarget);
    if (pgobTarget !== null) {
      this.ctx.host.NotifyShotFired(
        msgT.Hit.sideAssailant,
        pgobTarget.GetSide(),
        this.m_wx,
        this.m_wy,
      );
    }

    return true;
  }

  IsSavable(): boolean {
    return this.ctx.snapshotComplete;
  } // :258-261 (issue #98: see TankShotGob)

  override SaveState(pstm: import('../sm/stream.ts').Stream): boolean {
    pstm.WriteByte(1);
    this.m_li.SaveState(pstm);
    pstm.WriteWord(this.m_gidOwner);
    pstm.WriteWord(this.m_gidTarget);
    pstm.WriteWord(this.m_nDamage);
    this.m_ani.SaveState(pstm);
    return super.SaveState(pstm);
  }

  override LoadState(pstm: import('../sm/stream.ts').Stream): boolean {
    if (pstm.ReadByte() !== 1) return false;
    this.m_li.LoadState(pstm);
    this.m_gidOwner = pstm.ReadWord();
    this.m_gidTarget = pstm.ReadWord();
    this.m_nDamage = pstm.ReadWord();
    this.m_ani.Init(this.ctx.animDataFor('rocket.anir'));
    this.m_ani.LoadState(pstm);
    return super.LoadState(pstm);
  }

  // game/LRInfantry.cpp:312-390
  override ProcessStateMachineMessage(st: number, pmsg: Message): number {
    if (st === kstReservedGlobal && pmsg.mid === kmidReservedUpdate) {
      if (this.m_ani.GetStrip() === 0) {
        this.AdvanceAnimation(this.m_ani);

        if (this.m_li.Step()) {
          const wxOld = this.m_wx;
          const wyOld = this.m_wy;
          this.m_wx = this.m_li.GetWX();
          this.m_wy = this.m_li.GetWY();

          if (this.m_wx !== wxOld || this.m_wy !== wyOld) {
            this.ctx.gobm.MoveGob(this, wxOld, wyOld, this.m_wx, this.m_wy);
            this.MarkRedraw();
          }

          // A trail puff at the old position every other step (perf flag pinned ON):
          // each consumes a gid — sim-relevant
          if (this.ctx.wfPerfOptions & 0x0002 /* kfPerfRocketTrails */) {
            if (this.m_li.GetStepsRemaining() & 1) {
              const panid = this.ctx.animDataFor('rocket.anir');
              CreateAnimGob(
                this.ctx,
                wxOld,
                wyOld,
                kfAnmDeleteWhenDone | kfAnmSmokeFireLayer,
                panid,
                panid.GetStripIndex('trail'),
                ksmidNull,
              );
            }
          }

          this.m_unvl.MinSkip();
        } else {
          // owner-consts impact sfx — same C++-faithful recycled-gid
          // wrong-sound bound as ShotGob above (Rocket.cpp's blind cast)
          const punt = this.ctx.gobm.GetGob(this.m_gidOwner) as UnitGob | null;
          if (punt !== null) {
            this.ctx.host.PlaySfx(punt.GetConsts().sfxImpact);
          }

          this.StartAnimation(this.m_ani, 1, 0, 0);
          this.m_unvl.MinSkip();
        }
        // (kfPerfRocketShots redraw clear: presentation)
      } else {
        if (!this.AdvanceAnimation(this.m_ani)) {
          this.ctx.gobm.RemoveGob(this);
          return knDeleted;
        }
        // (kfPerfRocketImpacts redraw clear: presentation)
      }
      return knHandled;
    }
    return knNotHandled;
  }
}

// game/LRInfantry.cpp:155-176
export function CreateRocketGob(
  ctx: SimContext,
  wx: number,
  wy: number,
  wxTarget: number,
  wyTarget: number,
  nDamage: number,
  gidOwner: number,
  gidTarget: number,
): RocketGob | null {
  if (!ctx.gobm.IsBelowLimit(knLimitSupport)) return null;

  const pgob = new RocketGob(ctx);
  if (!pgob.InitRocket(wx, wy, wxTarget, wyTarget, nDamage, gidOwner, gidTarget)) return null;

  return pgob;
}

// self-register (see factory.ts; the C++ CreateGob switch includes kgtTankShot "probably
// unnecessarily" — kept for parity; shots are never level-loaded)
import { registerGobType } from './factory.ts';
registerGobType(kgtTankShot, (ctx) => new TankShotGob(ctx));
// issue #98: kgtBullet IS registered now — the TS COMPLETE snapshot serializes in-flight shots,
// so the resume load path must reconstruct them. Faithful saves never contain a shot (IsSavable
// is false unless snapshotComplete), so this never fires on a C++-format load.
registerGobType(kgtBullet, (ctx) => new BulletGob(ctx));
registerGobType(kgtRocket, (ctx) => new RocketGob(ctx));
